import React, { useMemo } from "react";
import BaseTable from "./BaseTable";
import { buildTable } from "../utils/tableBuilder";
import { generateDynamicColumns } from "../config/tableColumns";
import useProcessedTableData from "../hooks/useProcessedTableData";
import { formatBaseRecruitData } from "../utils/tableDataFormatters"; // Import the new base formatter
import { TableProvider } from "../hooks/useTableContext";

// Specific formatter for SummaryTable
const formatSummaryTableData = (recruits, formulasMapping = {}) => {
  return recruits.map((recruit) => {
    const formulas = formulasMapping[recruit.id];
    const baseData = formatBaseRecruitData(recruit, formulas);

    return {
      ...baseData,
      // Fields specific to SummaryTable
      email: recruit.email,
      motivation1: recruit.motivation1,
      motivation2: recruit.motivation2,
      motivation3: recruit.motivation3,
      motivation4: recruit.motivation4,
      output: 0, // Placeholder or specific calculation for SummaryTable
    };
  });
};

const tableId = "summary-table";

/**
 * SummaryTable Component - A wrapper for BaseTable with specific configurations
 * @param {Object} props - Component props
 * @param {Object} props.filter - Filter state
 * @param {boolean} props.columnsVisible - Whether all columns are visible
 * @param {boolean} props.headersRotated - Whether headers are rotated
 * @param {boolean} props.dataEncrypted - Whether data is encrypted
 * @param {Object} props.columnVisibility - Column visibility state
 * @param {function} props.setColumnVisibility - Function to set column visibility
 * @param {Array} props.pinnedColumns - Pinned columns
 * @param {function} props.setPinnedColumns - Function to set pinned columns
 * @param {Array} props.sorting - Sorting configuration
 * @param {function} props.setSorting - Function to set sorting
 * @param {Object} props.filterOptions - Filter options
 * @param {Object} props.setterFunctions - Filter setter functions
 * @param {Array} props.columns - Optional array of column IDs to include
 * @param {Object} props.dropdownData - Data for dropdowns, potentially used by BaseTable
 */
const SummaryTable = ({
  filter,
  columnsVisible,
  headersRotated = true,
  dataEncrypted = false,
  columnVisibility,
  setColumnVisibility,
  pinnedColumns = [],
  setPinnedColumns,
  sorting,
  setSorting,
  filterOptions = {},
  setterFunctions = {},
  columns = [], // This prop is now assumed to be always provided and non-empty
  dropdownData = {}, // Keep this prop if BaseTable or other parts use it
}) => {
  // Removed allPossibleColumns and defaultColumns

  const dynamicColumns = useMemo(() => {
    const pColumns = [];
    const fColumns = [];

    columns.forEach((col) => {
      if (col.startsWith("P")) {
        pColumns.push(col);
      } else if (col.startsWith("F")) {
        fColumns.push(col);
      }
    });

    return [...pColumns, ...fColumns];
  }, [columns]);

  const { processDataWithFormulas: memoizedFormatData } = useProcessedTableData(
    formatSummaryTableData
  );

  const tableConfig = useMemo(() => {
    // Directly use columns prop
    const columnsToUse = [...columns];

    Object.keys(generateDynamicColumns()).forEach((dynamicCol) => {
      if (
        !columnsToUse.includes(dynamicCol) &&
        dynamicColumns.includes(dynamicCol)
      ) {
        columnsToUse.push(dynamicCol);
      }
    });

    return buildTable(
      columnsToUse,
      {
        columnVisibility,
      },
      tableId
    );
  }, [columns, dynamicColumns, columnVisibility]);

  const initialColumnVisibility = useMemo(() => {
    const visibility = {};
    // Directly use columns prop
    columns.forEach((col) => {
      visibility[col] = true;
    });

    return { ...visibility, ...columnVisibility };
  }, [columns, columnVisibility]);

  return (
    <TableProvider
      tableId="summary-table"
      initialVisibility={initialColumnVisibility}
      initialColumns={columns} // Directly use columns prop
      externalColumnVisibility={columnVisibility}
      externalSetColumnVisibility={setColumnVisibility}
    >
      <BaseTable
        tableConfig={tableConfig}
        formatData={memoizedFormatData}
        tableId="summary-table"
        columnOrder={columns} // Directly use columns prop
        dropdownData={dropdownData}
        setterFunctions={setterFunctions}
        filter={filter}
        sorting={sorting}
      />
    </TableProvider>
  );
};

export default SummaryTable;
